import { Apollo } from '@api/Apollo';
import { AuthProvider } from '@authentication/Authentication';
import { ModalProvider } from '@components/modal';
import { PageLayout, PageLoader } from '@components/page';
import '@fontsource/lato/400.css';
import '@fontsource/lato/700.css';
import { devConsole } from '@helper-functions/devConsole';
import { MemberLookupProvider } from '@navigation/MemberLookupContext';
import { OrganizationContextProvider } from '@navigation/OrganizationContext';
import { WorkspaceContextProvider } from '@navigation/WorkspaceContext';
import { NotificationsProvider } from '@notifications/Notifications';
import { SeoProvider } from '@seo/SEO';
import { CookiesMainBar } from '@subsets/cookies';
import { GettingStartedProvider } from '@subsets/landing-page';
import { PostHogProvider } from 'posthog-js/react';
import 'react-loading-skeleton/dist/skeleton.css';
import { FeatureFlagProvider } from './src/hooks/FeatureFlags';
import { NetworkStatusProvider } from './src/hooks/NetworkStatusProvider';
import { Theme } from './src/theme/Theme';
import './src/theme/global.css';

// ---------------------------------------------------------------------------
// Browser bootstrap diagnostics — log key GATSBY_* values that were baked in
// at compile time. These are the actual values the browser will use for API
// calls, auth redirects, etc. If any show "undefined" or the wrong URL (e.g.
// localhost instead of a tunnel URL), the webpack cache was stale at compile
// time. Compare with the server-side log in `docker logs renderedai-deckard-dev`.
// ---------------------------------------------------------------------------
if (typeof window !== 'undefined') {
  devConsole.log(`[deckard-browser ${new Date().toISOString()}] Baked-in config:`);
  devConsole.log(`  GATSBY_AGENT_URL     = ${process.env.GATSBY_AGENT_URL}`);
  devConsole.log(`  GATSBY_GRAPHQL_URL   = ${process.env.GATSBY_GRAPHQL_URL}`);
  devConsole.log(`  GATSBY_KEYCLOAK_BASE_URL = ${process.env.GATSBY_KEYCLOAK_BASE_URL}`);
  devConsole.log(`  GATSBY_ENV           = ${process.env.GATSBY_ENV}`);
  devConsole.log(`  window.location      = ${window.location.origin}`);
}

const posthogOptions = {
  api_host: process.env.GATSBY_POSTHOG_HOST,
  // ui_host is required when using a reverse proxy to enable features like surveys, session recording, etc.
  // This points to the actual PostHog dashboard (not the proxy)
  ui_host: 'https://us.posthog.com',
  autocapture: false,
};

export const wrapPageElement = ({ element, props }) => {
  // Normalize pathname by stripping trailing slash so /register/ matches /register
  const pagePath = typeof window !== 'undefined' ? window.location.pathname.replace(/\/$/, '') : '';
  // Exact match for the Keycloak redirect page at /register
  const isRegisterPage = pagePath === '/register';

  // Custom Agent Studio signup pages under /register-as/*
  const isRegisterAsPage = pagePath.startsWith('/register-as');

  // Both register flows skip the full PageLayout wrapper
  if (isRegisterPage || isRegisterAsPage) {
    return element;
  }

  // Allow graph editor full width
  const fullWidth = props.graphId;

  return (
    <PageLoader>
      <OrganizationContextProvider organizationId={props?.organizationId}>
        <MemberLookupProvider>
          <WorkspaceContextProvider workspaceId={props?.workspaceId}>
            <ModalProvider>
              <PageLayout fullWidth={fullWidth}>{element}</PageLayout>
            </ModalProvider>
          </WorkspaceContextProvider>
        </MemberLookupProvider>
        <GettingStartedProvider />
      </OrganizationContextProvider>
      <CookiesMainBar />
    </PageLoader>
  );
};

export const wrapRootElement = ({ element }) => {
  // Normalize pathname by stripping trailing slash so /register/ matches /register
  const rootPath = typeof window !== 'undefined' ? window.location.pathname.replace(/\/$/, '') : '';
  // Exact match for the Keycloak redirect page at /register — AuthProvider only
  const isRegisterPage = rootPath === '/register';

  // Custom Agent Studio signup pages need Apollo (for GraphQL mutation) and Theme (for MUI)
  const isRegisterAsPage = rootPath.startsWith('/register-as');

  if (isRegisterPage) {
    return <AuthProvider>{element}</AuthProvider>;
  }

  // Register-as pages get a trimmed provider stack: Auth + Apollo + Theme
  if (isRegisterAsPage) {
    return (
      <AuthProvider>
        <Apollo>
          <Theme>{element}</Theme>
        </Apollo>
      </AuthProvider>
    );
  }

  return (
    <PostHogProvider apiKey={process.env.GATSBY_POSTHOG_KEY} options={posthogOptions}>
      <AuthProvider>
        <FeatureFlagProvider>
          <SeoProvider>
            <Apollo>
              <Theme>
                <NotificationsProvider>
                  <NetworkStatusProvider>{element}</NetworkStatusProvider>
                </NotificationsProvider>
              </Theme>
            </Apollo>
          </SeoProvider>
        </FeatureFlagProvider>
      </AuthProvider>
    </PostHogProvider>
  );
};
